import { createRef } from 'react';
import {IColumnsBlock, IContentBlock, IDesignBlock} from '../../types/iblock';
import { IColumnsBlockData } from '../../types/content-block-data';
import { escapeHTML } from '../../lib/page-builder-lib';
import pageBuilderStore from '../../stores/pageBuilderStore';
import blocksManager from '../../stores/blocks-manager';
import contentManager from '../../stores/content-manager';
import serializationManager from '../../stores/serialization-manager';
import { IFourSideField } from '../../stores/state-interfaces';

type Side = "top" | "right" | "bottom" | "left";

function getSidePaddingValue (field: IFourSideField, side: Side) {
	// If linked, use top for all sides, else use the specific side.
	const base = field.linked ? Number(field.top) : Number(field[side]);
	return isNaN(base) ? 0 : base;
}

/**
 * Converts a ColumnsBlock (IContentBlock) to an HTML string.
 * Recursively serializes all child blocks in each column.
 *
 * @param block - The IContentBlock (should be blockType "columns").
 * @param renderChildBlockToHTML - Function to serialize a child block to HTML
 * @returns HTML string representing the columns and their nested content blocks.
 * 
 * @remarks
 * - Added a new local variable `isEmail` for toggling serialization markup.
 */
export function fromColumnsBlockToHTML(
  block: IDesignBlock,
  renderChildBlockToHTML: (block: IDesignBlock) => string,
): string {
  if (block.contentBlock.blockType !== "columns") return "";

	const columnsCSS = contentManager.getStylesForSelector(block.contentBlock.selector!);

	const isEmail = pageBuilderStore.getIsEmail();
  const data = block.contentBlock.blockData as IColumnsBlockData;
  const colCount = data.columnWidths?.length || (data.children?.length || 1);
  const widths = data.columnWidths?.length === colCount
    ? data.columnWidths
    : Array(colCount).fill(1 / colCount);

  const columnsHTML = (data.children ?? []).map((blocks, idx) => {
    // Compute width as a percentage (for inline style)
    const width = widths[idx]
      ? `${widths[idx] * 100}%`
      : `${100 / (data.children?.length || 1)}%`;

		const unit = data.cellPadding?.unit || "px";
		const gap = Number(data.columnGap?.value);
		const halfGap = gap / 2;
		const totalColumns = data.children.length;

		// Get base paddings
		const paddingTop = getSidePaddingValue(data.cellPadding!, "top");
		const paddingRight = getSidePaddingValue(data.cellPadding!, "right");
		const paddingBottom = getSidePaddingValue(data.cellPadding!, "bottom");
		const paddingLeft = getSidePaddingValue(data.cellPadding!, "left");

		// Add gutter (gap) as per earlier logic
		let gutterLeft = 0, gutterRight = 0;
		if (totalColumns === 1) {
			gutterLeft = 0; gutterRight = 0;
		} else if (idx === 0) {
			gutterLeft = 0; gutterRight = halfGap;
		} else if (idx === totalColumns - 1) {
			gutterLeft = halfGap; gutterRight = 0;
		} else {
			gutterLeft = halfGap; gutterRight = halfGap;
		}


		let cellStyle = `padding: ${isEmail 
			? `${paddingTop}${unit} ${paddingRight + gutterRight}${unit} ${paddingBottom}${unit} ${paddingLeft + gutterLeft}${unit}` 
			: blocksManager.dimensionToCss(data.cellPadding)};`;
		cellStyle += `margin: ${blocksManager.dimensionToCss(data.cellMargin)};`;
		cellStyle += `border-radius: ${blocksManager.dimensionToCss(data.cellRadius)};`;
		cellStyle += `overflow: ${parseInt(data.cellRadius?.topLeft!) > 0 
			|| parseInt(data.cellRadius?.topRight!) > 0
			|| parseInt(data.cellRadius?.bottomLeft!) > 0
			|| parseInt(data.cellRadius?.bottomRight!) > 0  ? "hidden" : "auto"};`;
		cellStyle += `width: ${width};`

    const openTag = `<div class="${block.contentBlock.selector}-cell" style="${cellStyle}">`;
    const closeTag = `</div>`;
    const contentHTML = blocks.map(renderChildBlockToHTML).join("");
		if (isEmail) {
			let gapCell = "";
			if (idx < data.children?.length - 1) {
				gapCell = `<td width="${blocksManager.dimensionToCss(data.columnGap)}" style="font-size:0; line-height:0;"></td>`;
			}
			return `<td style="${cellStyle} vertical-align:top;">
				<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0">
					${contentHTML}
				</table>
			</td>`;
		} else {
    	return `${openTag}${contentHTML}${closeTag}`;
		}
  }).join('');

	let columnStyle = `display: grid; grid-template-columns: repeat(${colCount}, 1fr); `;
	columnStyle += `gap: ${blocksManager.dimensionToCss(data.columnGap)}; `;
	columnStyle += `row-gap: ${blocksManager.dimensionToCss(data.rowGap)}; `;

  if (isEmail) {
		// Will fill this out next
		return `<tr><td><table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0">
			<tr>
				${columnsHTML}
			</tr>
		</table></td></tr>`;
	} else {
		return `<div class="${block.contentBlock.selector}" style="${columnStyle}">${columnsHTML}</div>`;
	}
}

/**
 * Serializes a ColumnsBlock's data for storage or export.
 * Omits UI-only fields and recursively serializes children (as IContentBlock[]).
 *
 * @param block - The IContentBlock (should be blockType "columns")
 * @param childToJSON - Function to serialize a child block (IDesignBlock) to JSON
 * @returns A JSON-serializable object
 */
export function fromColumnsBlockToJSON(
	block: IContentBlock,
	childToJSON: (block: IContentBlock) => object
): object {
	if (block.blockType !== "columns") return {};
	
	const data = block.blockData as IColumnsBlockData;
	
	return {
		contentBlockId: block.contentBlockId,
		blockType: block.blockType,
		selector: block.selector,
		blockData: {
			children: (data.children ?? []).map(column =>
				column.map((childBlock: IDesignBlock) => childToJSON(childBlock.contentBlock))
			),
			columnWidths: data.columnWidths,
		},
	};
}

/**
 * Deserializes a ColumnsBlock from plain JSON data into an IDesignBlock.
 * Recursively hydrates children as IDesignBlock[].
 */
export function fromJSONToColumnsBlock(
	json: any,
): IDesignBlock {
	const cssStyles = contentManager.getStylesForSelector(json.selector);

	const newDesignBlockRef = createRef<HTMLDivElement>();
	const newContentBlockRef = createRef<HTMLElement>(); // generic for now

	const block: IDesignBlock = {
		designBlockId: crypto.randomUUID(), // always generate fresh
		designBlockRef: newDesignBlockRef,
		contentBlock: {
			blockType: json.blockType,
			contentBlockId: json.contentBlockId ?? crypto.randomUUID(),
			contentBlockRef: newContentBlockRef, // set in UI code if needed
			selector: json.selector,
			blockData: {
				children: json.blockData.children.map((column: any[]) => {
					const deserializedChildren: IDesignBlock[] = [];
					for (let i = 0; i < column.length; i++) {
						const child = column[i];
						deserializedChildren.push(serializationManager.fromJSONToContentBlock(child, "columns"));
						console.log(child);
					}
					return deserializedChildren;
				}),
				columnGap: blocksManager.parseDimensions(cssStyles?.['gap'], "single"),
				rowGap:blocksManager.parseDimensions(cssStyles?.['row-gap'], "single"),
        columnWidths: json.blockData?.columnWidths,
				cellMargin: blocksManager.parseDimensions(contentManager.getStylesForSelector(json.selector + "-cell")?.['margin'], "four-side"),
				cellPadding: blocksManager.parseDimensions(contentManager.getStylesForSelector(json.selector + "-cell")?.['padding'], "four-side"),
				cellRadius: blocksManager.parseDimensions(contentManager.getStylesForSelector(json.selector + "-cell")?.['border-radius'], "four-corners"),
			} as IColumnsBlockData,
		},
	};

	blocksManager.addDesignTreeEntry({
		contentBlockId: block.contentBlock.contentBlockId,
		designBlockId: block.designBlockId,
		designBlockRef: newDesignBlockRef,
		contentBlockRef: newContentBlockRef,
		contentBlockType: block.contentBlock.blockType.toUpperCase(),
		location: "canvas",
	});

	return block;
}